// =============================================================
// GlassCard
// -------------------------------------------------------------
// Glassmorphism-styled card for the Training Institute analytics
// dashboard. The look is built on three layers:
//   1. Translucent background (`backdrop-filter: blur`) so the
//      content behind shows through softly.
//   2. A thin white border at low alpha for a frosted-edge feel.
//   3. A subtle drop shadow that lifts the card off the page.
//
// All three layers can be tuned via props, but sensible defaults
// are used everywhere. The card supports a coloured accent strip
// (top bar) for visual categorisation.
//
// `accent` accepts any CSS colour and is used to tint the bottom
// accent line and the optional icon background.
// =============================================================

import React from 'react';

export default function GlassCard({
  title,
  subtitle,
  icon,
  accent = '#3b82f6',           // blue-500 — default accent colour
  accentPosition = 'bottom',    // 'bottom' | 'top' | 'left'
  badge,
  extra,
  height,                       // optional body min-height
  onClick,
  noPadding = false,
  className = '',
  children,
  bodyStyle,
  intensity = 'medium',         // 'low' | 'medium' | 'high' controls blur
}) {
  const interactive = !!onClick;
  const blur = intensity === 'low' ? 8 : intensity === 'high' ? 24 : 16;
  const bgAlpha = intensity === 'low' ? 0.45 : intensity === 'high' ? 0.7 : 0.6;

  return (
    <div
      className={`card h-100 glass-card ${className}`}
      style={{
        position: 'relative',
        background: `rgba(255, 255, 255, ${bgAlpha})`,
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`,
        border: '1px solid rgba(255, 255, 255, 0.45)',
        borderRadius: 18,
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18), inset 0 1px 0 0 rgba(255,255,255,0.6)',
        cursor: interactive ? 'pointer' : 'default',
        transition: 'transform .22s ease, box-shadow .22s ease, border-color .22s ease',
        overflow: 'hidden',
      }}
      onClick={onClick}
      onMouseEnter={interactive ? (e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = `0 14px 40px 0 ${accent}33, inset 0 1px 0 0 rgba(255,255,255,0.6)`;
        e.currentTarget.style.borderColor = `${accent}55`;
      } : undefined}
      onMouseLeave={interactive ? (e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 8px 32px 0 rgba(31, 38, 135, 0.18), inset 0 1px 0 0 rgba(255,255,255,0.6)';
        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.45)';
      } : undefined}
    >
      <div className="card-body" style={{ padding: noPadding ? 0 : '18px 18px 16px' }}>
        {(title || icon || badge) && (
          <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
            <div className="d-flex align-items-center gap-2">
              {icon && (
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 38, height: 38,
                    background: `linear-gradient(135deg, ${accent}22 0%, ${accent}11 100%)`,
                    border: `1px solid ${accent}33`,
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <i className={`bi ${icon}`} style={{ fontSize: 18, color: accent }}></i>
                </div>
              )}
              <div>
                {title && (
                  <div style={{
                    fontWeight: 700, fontSize: 14, color: '#1e3a8a',
                    lineHeight: 1.2, letterSpacing: 0.2,
                  }}>
                    {title}
                  </div>
                )}
                {subtitle && (
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, fontWeight: 500 }}>
                    {subtitle}
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              {badge}
              {extra}
            </div>
          </div>
        )}
        <div style={{ minHeight: height, ...bodyStyle }}>
          {children}
        </div>
      </div>
      {/* Accent strip — top, bottom, or left edge */}
      {accent && (
        <div
          style={{
            position: 'absolute',
            ...(accentPosition === 'top' && { top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}88)` }),
            ...(accentPosition === 'bottom' && { bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}88, ${accent})` }),
            ...(accentPosition === 'left' && { left: 0, top: 0, bottom: 0, width: 4, background: `linear-gradient(180deg, ${accent}, ${accent}88)` }),
            opacity: 0.85,
          }}
        />
      )}
    </div>
  );
}
